import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef }
	from '@angular/material/dialog';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BlobService } from '../../../shared/utils/blob.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user';
import { OrderService } from '../../services/order.service';
import { HashService } from '../../../shared/services/hash.service';
import { UNDERSCORE } from '../../../shared/utils/constant';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { DragDropFilesDirective } from '../../../shared/directives/drag-drop-files.directive';
import { SnackbarSubmissionFeedbackComponent }
	from '../../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { TranslationService } from '../../../shared/services/translation.service';

import { ElinkData } from './elink-data';

interface ElinkFormModel {
	copyTo: string;
	messageSubject: string;
	textMessage: string;
}

function checkFileSize(file: File) {
	return file.size <= 1e+7;
}

@Component({
	selector: 'copo-elink',
	templateUrl: './elink.component.html',
	styleUrl: './elink.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,

		MatFormFieldModule,

		MatInputModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		DragDropFilesDirective,
		NgFor,
		DatePipe,
		NumberPipe,
		DimensionPipe,
		SafeUrlPipe,
		MatDialogModule
	]
})
export class ElinkComponent implements OnInit {
	elinkForm: FormGroup;
	uploadSub?: Subscription;
	contactSub?: Subscription;
	status: string;
	filePath?: string;
	files: File[];
	fileUrls = new WeakMap<File, string>();
	readonly email: string;
	readonly firstname: string;
	readonly lastname: string;
	readonly gender?: string;
	readonly customer: string;
	readonly subject: string;
	readonly UNDERSCORE = UNDERSCORE;
	private readonly attachmentMap: Map<string, string>;
	private readonly user?: User;

	constructor(

		private readonly dialogRef: MatDialogRef<ElinkComponent>,

		@Inject(MAT_DIALOG_DATA) public data: ElinkData,
		private readonly userService: UserService,

		private readonly snackBar: MatSnackBar,
		private readonly blobService: BlobService,
		private readonly orderService: OrderService,
		private readonly hashService: HashService,
		private readonly translationService: TranslationService,
	) {
		this.user = this.userService.user;
		this.email = this.user.email;
		this.firstname = this.user.firstname;
		this.lastname = this.user.lastname;
		this.gender = this.user.gender.charAt(0).toUpperCase() + this.user.gender.slice(1);
		this.customer = this.user.currentVisibility.organizationName;
		this.files = [];
		this.filePath = '';
		this.status = '';
		this.attachmentMap = new Map<string, string>();
		this.subject = this.constructSubject();
		this.elinkForm = new FormGroup({
			copyTo: new FormControl(),
			messageSubject: new FormControl(),
			textMessage: new FormControl()
		});
	}

	onCancelClick() {
		this.dialogRef.close();
	}

	ngOnInit() {
		this.elinkForm = new FormGroup({
			copyTo: new FormControl('', [this.commaSepEmail]),
			messageSubject: new FormControl(),
			textMessage: new FormControl('', [Validators.required]),
		});
	}

	commaSepEmail = (control: AbstractControl) => {
		const formValue = <string> control.value;
		const emails = formValue.split(',').map(e => e.trim());
		const forbidden = emails.some(email => Boolean(Validators.email(new FormControl(email))));
		return forbidden ? { copyTo: { value: formValue } } : null;
	};

	async sendMessage(form: FormGroup) {
		for (const file of this.files) {
			const hash = await this.hashService.readSHA1(file);
			this.uploadSub = this.orderService.uploadFile(file, hash).subscribe({
				next: path => {
					this.filePath = path;
				}
			});
			this.attachmentMap.set(hash, file.name);
		}

		const mailContent = this.constructMailContent(form);

		this.contactSub = this.orderService.contactMill(mailContent).subscribe({
			next: status => {
				this.status = status;
			}
		});
		this.onCancelClick();
		this.snackBar.openFromComponent(
			SnackbarSubmissionFeedbackComponent,
			{
				data: {
					hideResponseDetails: true,
					isThanksMessage: true,
					isRedirectLink: true,
					submitThanksMessage: this.translationService.getTR('contactmail.popup.success.msg-line1'),
					orderSavedText1: this.translationService.getTR('contactmail.popup.success.msg-line2')
				},
				duration: 10_000,
				panelClass: ['copo-snackbar-submission-feedback-container']
			}
		);
		return true;
	}

	uploadFile(events: FileList | null) {
		if (!events) {
			return;
		}
		const files = Array.from(events);

		if ((this.files.length + events.length > 3)
			|| events.length > 3
			|| this.files.length >= 3) {
			return;
		}
		for (const file of files) {
			if (checkFileSize(file)) {
				this.files.push(file);
			}
		}
	}

	deleteAttachment(index: number) {
		if (this.files.length === 1) {
			this.files = [];
			return;
		}
		this.files.splice(index, 1);
	}

	getFileUrl(file: File) {
		const url = this.fileUrls.get(file);
		if (url) {
			return url;
		}
		const newUrl = URL.createObjectURL(file);
		this.fileUrls.set(file, newUrl);
		return newUrl;
	}

	async showFile(event: MouseEvent, file: File) {
		const { href } = <HTMLAnchorElement> event.currentTarget;
		if (!href.startsWith('blob:') || !('msSaveOrOpenBlob' in navigator)) {
			return;
		}
		event.preventDefault();
		const blob = await this.blobService.getBlob(href);
		navigator.msSaveOrOpenBlob(blob, file.name);
	}

	private constructSubject() {
		return this.data.soldTo.replace(/^0+/, '').concat(' - ').concat(this.data.mill);
	}

	private constructMailContent(form: FormGroup) {
		const formValue = <ElinkFormModel> form.value;
		const copyToValue = formValue.copyTo;
		const subjectValue = formValue.messageSubject;
		const textMessageValue = formValue.textMessage;
		const subject = subjectValue ? this.subject.concat(' ').concat(subjectValue) : this.subject;

		const contacts = copyToValue ? copyToValue.split(',').map(e => e.trim()) : undefined;
		return {
			millCode: this.data.millCode,
			subject,
			messageContent: textMessageValue,
			customerOrderNumber: this.data.customerOrderNumber,
			customerOrderItemNumber: this.data.customerOrderItemNumber,
			supplierOrderNumber: this.data.supplierOrderNumber,
			supplierOrderItemNumber: this.data.supplierOrderItemNumber,
			millOrderNumber: this.data.millOrderNumber,
			millOrderItemNumber: this.data.millOrderItemNumber,
			mill: this.data.mill,
			customer: this.customer,
			product: this.data.productCategory,
			thickness: this.data.thickness,
			width: this.data.width,
			ordered: this.data.orderedQuantity,
			confirmedDeliveryDate: this.data.cdd,
			contacts,
			attachments: Object.fromEntries(this.attachmentMap.entries())
		};
	}
}
